import { QueryOptions, WithRequired } from '@tanstack/react-query';

import {
  getAllFlowMessagesNeedDeliverabilityBlockUpgrade,
  getAllMisconfiguredExperiments,
  getAllNeedUniversalBlockExperiments,
  getAllPendingFlowMessages,
  getBrowserAutomationSessions,
  getCampaignCoverageAlerts,
  getCSBrowserSessions,
  getCampaignMonthlyDeliverabilityMetrics,
  getCampaignPrimaryConversionMetricOptions,
  getCampaignSetupAutomationPauseStatus,
  getCombinedMonthlyOptimizerMetrics,
  getCustomerFeedbacksForStore,
  getCustomerFeedbacksForUsecase,
  getDataQualityReport,
  getEnabledStores,
  getFlowActionsForStoreKey,
  getFlowMessage,
  getFlowMessageExperimentAllocationConversionStats,
  getFlowMessageExperimentTargetMetrics,
  getFlowMessagesForStoreKey,
  getFlowMonthlyOptimizerMetrics,
  getFlowsForStoreKey,
  getGrowthReport,
  getKlaviyoAccount,
  getOptimizerCampaignDeliverabilityExperimentMetrics,
  getOrderRateLiftAlerts,
  getOrganizationMembers,
  getPretesterInboxes,
  getSPOptimizerCampaignDeliverabilityBlockSetups,
  getSPOptimizerFlowMessageExperimentKlaviyoTemplates,
  getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore,
  getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics,
  getSPOptimizerFlowMessageExperimentVariantsForExperiment,
  getSPOptimizerFlowMessageExperimentsForFlowMessage,
  getSPOptimizerFlowMessageExperimentsForSPOptimizerStore,
  getSPOptimizerFlowMessageExperimentsForStore,
  getSPOptimizerStore,
  getSPOptimizerStoreAggregatedSummaryFlowMetrics,
  getSPOptimizerStoreBatchAggregatedSummaryFlowMetrics,
  getSPOptimizerStoreDeliverabilityBlockSetup,
  getSPOptimizerStoreDeliverabilityBlockSetups,
  getSPOptimizerStoreExcludePhrases,
  getSPOptimizerStoreIncludePhrases,
  getSPOptimizerStores,
  getSavedVideoScripts,
  getSetupDecisions,
  getShopifyShop,
  getSlackAnnouncement,
  getSlackAnnouncements,
  getSlackChannelMappings,
  getStoreAggregatedMetrics,
  getStoreAggregatedMetricsPerUseCase,
  getStoreDailyMetrics,
  getStores,
  getTrialExpirations,
  getTrialExpirationsIncrementalRevenue,
  getUsecase,
  getUsecasesForStore,
  postShopifyCartProductImages,
} from './methods';

const makeQuery = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions: (
    ...args: [...Parameters<F>]
  ) => Omit<WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryKey'>, 'queryFn'>,
): ((...args: [...Parameters<F>]) => WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryFn' | 'queryKey'>) => {
  return (...args) => ({
    queryFn: () => apiMethod(...args),
    ...otherOptions(...args),
  });
};

export const queries = {
  stores: makeQuery(getStores, () => ({
    queryKey: ['stores'],
  })),
  growthReport: makeQuery(getGrowthReport, (product: 'all' | 'video' | 'optimizer') => ({
    queryKey: ['growth-report', product],
  })),
  enabledStores: makeQuery(getEnabledStores, () => ({
    queryKey: ['enabled-stores'],
  })),
  trialExpirations: makeQuery(getTrialExpirations, () => ({
    queryKey: ['trial-expirations'],
  })),
  trialExpirationsIncrementalRevenue: makeQuery(getTrialExpirationsIncrementalRevenue, () => ({
    queryKey: ['trial-expirations-incremental-revenue'],
  })),
  usecase: makeQuery(getUsecase, (organization: string, usecaseId: number) => ({
    queryKey: ['organization', organization, 'usecase', usecaseId],
  })),
  flowMessage: makeQuery(getFlowMessage, (flowMessageID: number) => ({
    queryKey: ['flow-message', flowMessageID],
  })),
  usecasesForStore: makeQuery(
    getUsecasesForStore,
    (organization: string | undefined, storeKey: string | undefined) => ({
      queryKey: ['organization', organization, 'usecases', storeKey],
    }),
  ),
  spOptimizerStores: makeQuery(getSPOptimizerStores, (organization: string) => ({
    queryKey: ['organization', organization, 'spOptimizerStores'],
  })),
  spOptimizerStore: makeQuery(getSPOptimizerStore, (organization: string, spOptimizerStoreID: number) => ({
    queryKey: ['organization', organization, 'spOptimizerStore', spOptimizerStoreID],
  })),
  spOptimizerStoreExcludePhrases: makeQuery(
    getSPOptimizerStoreExcludePhrases,
    (organization: string, spOptimizerStoreID: number) => ({
      queryKey: ['organization', organization, 'spOptimizerStoreExcludePhrases', spOptimizerStoreID],
    }),
  ),
  spOptimizerStoreIncludePhrases: makeQuery(
    getSPOptimizerStoreIncludePhrases,
    (organization: string, spOptimizerStoreID: number) => ({
      queryKey: ['organization', organization, 'spOptimizerStoreIncludePhrases', spOptimizerStoreID],
    }),
  ),
  spOptimizerStoreDeliverabilityBlockSetups: makeQuery(getSPOptimizerStoreDeliverabilityBlockSetups, () => ({
    queryKey: ['spOptimizerStoreDeliverabilityBlockSetups'],
  })),
  spOptimizerStoreDeliverabilityBlockSetup: makeQuery(
    getSPOptimizerStoreDeliverabilityBlockSetup,
    (spOptimizerStoreID: number) => ({
      queryKey: ['spOptimizerStoreDeliverabilityBlockSetup', spOptimizerStoreID],
    }),
  ),
  spOptimizerCampaignDeliverabilityBlockSetups: makeQuery(
    getSPOptimizerCampaignDeliverabilityBlockSetups,
    (setupStatuses: string[], page = 1, pageSize = 50, storeID?: number) => ({
      queryKey: ['spOptimizerCampaignDeliverabilityBlockSetups', setupStatuses, page, pageSize, storeID],
    }),
  ),
  campaignSetupAutomationPauseStatus: makeQuery(getCampaignSetupAutomationPauseStatus, () => ({
    queryKey: ['campaignSetupAutomationPauseStatus'],
  })),
  storeAggregatedMetrics: makeQuery(
    getStoreAggregatedMetrics,
    (organization: string, storeKey: string, days: number, useCaseId?: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'usecase', useCaseId, 'aggregate-metrics', days],
    }),
  ),
  storeAggregatedMetricsPerUseCase: makeQuery(
    getStoreAggregatedMetricsPerUseCase,
    (organization: string, storeKey: string, days: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'aggregate-metrics-per-usecase', days],
    }),
  ),
  storeDailyMetrics: makeQuery(
    getStoreDailyMetrics,
    (organization: string, storeKey: string, days: number, useCaseId?: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'usecase', useCaseId, 'daily-metrics', days],
    }),
  ),
  storeFlowMonthlyOptimizerMetrics: makeQuery(
    getFlowMonthlyOptimizerMetrics,
    (organization: string | undefined, spOptimizerStoreID: number | undefined) => ({
      queryKey: ['organization', organization, 'spOptimizerStore', spOptimizerStoreID, 'flow', 'monthly-metrics'],
    }),
  ),
  storeCampaignMonthlyDeliverabilityMetrics: makeQuery(
    getCampaignMonthlyDeliverabilityMetrics,
    (organization: string, spOptimizerStoreId: number | undefined) => ({
      queryKey: [
        'organization',
        organization,
        'spOptimizerStore',
        spOptimizerStoreId,
        'campaign-deliverability',
        'monthly-metrics',
      ],
    }),
  ),
  storeCombinedMonthlyOptimizerMetrics: makeQuery(
    getCombinedMonthlyOptimizerMetrics,
    (
      organization: string,
      spOptimizerStoreId: number | undefined,
      includeCampaigns?: boolean,
      includeFlows?: boolean,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'spOptimizerStore',
        spOptimizerStoreId,
        'combined',
        'monthly-metrics',
        includeCampaigns,
        includeFlows,
      ],
    }),
  ),
  flowMessageExperimentAllocationConversionStats: makeQuery(
    getFlowMessageExperimentAllocationConversionStats,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'flow-message-experiment-allocation-conversion-stats',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  flowMessageExperimentTargetMetrics: makeQuery(
    getFlowMessageExperimentTargetMetrics,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'flow-message-experiment-target-metrics',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  customerFeedbacksForUsecase: makeQuery(
    getCustomerFeedbacksForUsecase,
    (organization: string, usecaseId: number, page = 1, pageSize = 10) => ({
      queryKey: ['organization', organization, 'usecase', 'user-feedback', usecaseId, page, pageSize],
    }),
  ),
  customerFeedbacksForStore: makeQuery(
    getCustomerFeedbacksForStore,
    (organization: string, storeId: number | undefined, page = 1, pageSize = 25) => ({
      queryKey: ['organization', organization, 'store', 'user-feedback', storeId, page, pageSize],
    }),
  ),
  klaviyoAccount: makeQuery(getKlaviyoAccount, (organization: string, klaviyoAccountId: number) => ({
    queryKey: ['organization', organization, 'klaviyo-account', klaviyoAccountId],
  })),
  shopifyShop: makeQuery(getShopifyShop, (organization: string, shopifyShopId: number) => ({
    queryKey: ['organization', organization, 'shopify-shop', shopifyShopId],
  })),
  shopifyCartProductImages: makeQuery(
    postShopifyCartProductImages,
    (
      organization: string,
      storeKey: string,
      shopifyCheckoutId: string | undefined | null,
      shopifyCheckoutToken: string | undefined | null,
      shopifyProductIds: string[],
    ) => ({
      queryKey: [
        'organization',
        organization,
        'store',
        storeKey,
        'shopify-cart-product-images',
        shopifyCheckoutId,
        shopifyCheckoutToken,
        shopifyProductIds,
      ],
    }),
  ),
  savedVideoScripts: makeQuery(getSavedVideoScripts, (organization: string, useCaseId: number) => ({
    queryKey: ['organization', organization, 'usecase', useCaseId, 'get-saved-video-scripts'],
  })),
  spOptimizerFlowMessageExperimentsForSPOptimizerStore: makeQuery(
    getSPOptimizerFlowMessageExperimentsForSPOptimizerStore,
    (organization: string, spOptimizerStoreId: number | undefined) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiments',
        'for-sp-optimizer-store',
        spOptimizerStoreId,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentsForStore: makeQuery(
    getSPOptimizerFlowMessageExperimentsForStore,
    (organization: string | undefined, storeKey: string | undefined) => ({
      queryKey: ['organization', organization, 'sp-optimizer-flow-message-experiments', 'for-store', storeKey],
    }),
  ),
  spOptimizerFlowMessageExperimentsForFlowMessage: makeQuery(
    getSPOptimizerFlowMessageExperimentsForFlowMessage,
    (organization: string | undefined, flowMessageID: number | undefined) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiments',
        'for-flow-message',
        flowMessageID,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentMetricsForSPOptimizerStore: makeQuery(
    getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore,
    (organization: string, spOptimizerStoreId: number) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-metrics',
        'for-sp-optimizer-store',
        spOptimizerStoreId,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentVariantAggregatedMetrics: makeQuery(
    getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics,
    (
      organization: string,
      spOptimizerFlowMessageExperimentVariantId: number,
      days: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-variant',
        spOptimizerFlowMessageExperimentVariantId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  spOptimizerStoreAggregatedSummaryFlowMetrics: makeQuery(
    getSPOptimizerStoreAggregatedSummaryFlowMetrics,
    (
      organization: string,
      spOptimizerStoreId: number,
      days: number,
      flowId?: number,
      flowMessageId?: number,
      flowMessageVariantId?: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-store-aggregated-summary-flow-metrics',
        spOptimizerStoreId,
        'flow',
        flowId,
        'flow-message',
        flowMessageId,
        'flow-message-variant',
        flowMessageVariantId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  spOptimizerStoreBatchAggregatedSummaryFlowMetrics: makeQuery(
    getSPOptimizerStoreBatchAggregatedSummaryFlowMetrics,
    (
      organization: string,
      spOptimizerStoreId: number,
      days: number,
      flowId?: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-store-batch-aggregated-summary-flow-metrics',
        spOptimizerStoreId,
        'flow',
        flowId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  flowsForStoreKey: makeQuery(getFlowsForStoreKey, (storeKey: string) => ({
    queryKey: ['flows-for-store-key', storeKey],
  })),
  flowMessageForStoreKey: makeQuery(getFlowMessagesForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-messages-for-store-key', storeKey],
  })),
  flowActionsForStoreKey: makeQuery(getFlowActionsForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-actions-for-store-key', storeKey],
  })),
  spOptimizerFlowMessageExperimentVariantsForExperiment: makeQuery(
    getSPOptimizerFlowMessageExperimentVariantsForExperiment,
    (organization: string, spOptimizerFlowMessageExperimentId: number, onlyActive?: boolean) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-variants-for-experiment',
        spOptimizerFlowMessageExperimentId,
        'only-active',
        onlyActive,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentKlaviyoTemplates: makeQuery(
    getSPOptimizerFlowMessageExperimentKlaviyoTemplates,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-klaviyo-templates',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  organizationMembers: makeQuery(getOrganizationMembers, (slug: string) => ({
    queryKey: ['organization', slug, 'members'],
  })),
  pretesterInboxes: makeQuery(getPretesterInboxes, () => ({
    queryKey: ['pretester-inboxes'],
  })),
  slackChannelMappings: makeQuery(getSlackChannelMappings, () => ({
    queryKey: ['slack-channel-mappings'],
  })),
  slackAnnouncements: makeQuery(getSlackAnnouncements, (page = 1) => ({
    queryKey: ['slack-announcements', page],
    // Poll only while at least one announcement is still in flight
    // (PENDING/PROCESSING). Once everything resolves the page goes quiet, so
    // history-only viewers don't generate continuous load.
    refetchInterval: (query: { state: { data?: Awaited<ReturnType<typeof getSlackAnnouncements>> } }) => {
      const rows = query.state.data?.results ?? [];
      const inFlight = rows.some((r) => r.status === 'PENDING' || r.status === 'PROCESSING');
      return inFlight ? 30_000 : false;
    },
  })),
  slackAnnouncement: makeQuery(getSlackAnnouncement, (id: number) => ({
    queryKey: ['slack-announcement', id],
    // Retrieve has its own poll because the app's QueryClient sets
    // staleTime: Infinity and disables refetch-on-mount; without this an
    // expanded row's per-channel deliveries would never refresh.
    refetchInterval: (query: { state: { data?: Awaited<ReturnType<typeof getSlackAnnouncement>> } }) => {
      const deliveries = query.state.data?.deliveries ?? [];
      const inFlight = deliveries.some((d) => d.status === 'PENDING' || d.status === 'CLAIMED');
      return inFlight ? 30_000 : false;
    },
  })),
  browserAutomationSessions: makeQuery(getBrowserAutomationSessions, (page = 1, pageSize = 50) => ({
    queryKey: ['browser-automation-sessions', page, pageSize],
    // Fast poll while a session is live (the iframe viewer wants fresh status); slow poll
    // otherwise so newly started sessions still appear. Global QueryClient sets staleTime:Infinity.
    refetchInterval: (query: { state: { data?: Awaited<ReturnType<typeof getBrowserAutomationSessions>> } }) => {
      const rows = query.state.data?.results ?? [];
      const running = rows.some((r) => r.status === 'RUNNING');
      return running ? 5_000 : 30_000;
    },
  })),
  csBrowserSessions: makeQuery(getCSBrowserSessions, (identity: 'team' | 'support', pageSize = 20) => ({
    queryKey: ['cs-browser-sessions', identity, pageSize],
    refetchInterval: 30_000,
  })),
  campaignCoverageAlerts: makeQuery(getCampaignCoverageAlerts, (weekStart?: string) => ({
    queryKey: ['campaign-coverage-alerts', weekStart ?? 'latest'],
  })),
  pendingFlowMessages: makeQuery(
    getAllPendingFlowMessages,
    (page = 1, pageSize = 200, tab: 'pending' | 'skipped' = 'pending', storeKeyName?: string) => ({
      queryKey: ['pending-flow-messages', page, pageSize, tab, storeKeyName],
    }),
  ),
  orderRateLiftAlerts: makeQuery(getOrderRateLiftAlerts, (weekStart?: string) => ({
    queryKey: ['order-rate-lift-alerts', weekStart ?? 'latest'],
  })),
  setupDecisions: makeQuery(getSetupDecisions, (filters) => ({
    queryKey: ['setup-decisions', filters?.status ?? 'all', filters?.spOptimizerStore ?? 'all'],
  })),
  misconfiguredExperiments: makeQuery(getAllMisconfiguredExperiments, () => ({
    queryKey: ['misconfigured-experiments'],
  })),
  dataQualityReport: makeQuery(getDataQualityReport, () => ({
    queryKey: ['data-quality-report'],
  })),
  needUniversalBlockExperiments: makeQuery(
    getAllNeedUniversalBlockExperiments,
    (page = 1, storeKeyName?: string, flowMessageId?: string) => ({
      queryKey: ['need-universal-block-experiments', page, storeKeyName, flowMessageId],
    }),
  ),
  flowMessagesNeedDeliverabilityBlockUpgrade: makeQuery(
    getAllFlowMessagesNeedDeliverabilityBlockUpgrade,
    (page = 1, storeKeyName?: string, flowMessageId?: string) => ({
      queryKey: ['flow-messages-need-deliverability-block-upgrade', page, storeKeyName, flowMessageId],
    }),
  ),
  campaignPrimaryConversionMetricOptions: makeQuery(
    getCampaignPrimaryConversionMetricOptions,
    (organization: string, spOptimizerStoreId: number) => ({
      queryKey: ['organization', organization, 'spOptimizerStore', spOptimizerStoreId, 'campaign-pcm-options'],
    }),
  ),
  optimizerCampaignDeliverabilityExperimentMetrics: makeQuery(
    getOptimizerCampaignDeliverabilityExperimentMetrics,
    (
      organization: string,
      spOptimizerStoreID: number,
      days: number,
      startDate?: string,
      endDate?: string,
      includeLikelihood = false,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'optimizer-campaign-deliverability-experiment-results',
        spOptimizerStoreID,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
        'include-likelihood',
        includeLikelihood,
      ],
    }),
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<string, (...args: any[]) => WithRequired<QueryOptions<any>, 'queryKey' | 'queryFn'>>;
